/**
 * Resolve the Firebase token for SmallApp REST microservice calls.
 *
 * On share-link pages, the token must carry the `shareLinkObjectId` claim so
 * SmallApp REST's `verifyAccessTokenFirebase` proxy → NestJS `getMe(token)` →
 * `GqlAuthGuard._augmentWithShareLinkContext` augments `tokenData.accountId`
 * to the share owner's accountId. Without the claim, cross-company users
 * resolve to their REAL accountId → asset.accountId mismatch → 403
 * "Access denied" / "Invalid access token".
 *
 * Backend verifies share-link access (securities/email/ANYONE/ACCOUNT) before
 * augmenting, so the claim alone does not grant unauthorized access.
 */
import type { ApolloClient } from '@apollo/client';
import { getTokenString } from '@/lib/firebase';
import { forceRefreshShareLinkToken } from '@/features/share-links/utils/share-auth-helpers';

export async function getSmallAppRestToken(client: ApolloClient): Promise<string | null> {
  const isShareContext =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/share/');
  const token = await getTokenString();
  if (!isShareContext || !token) return token;

  const objectId = window.location.pathname.replace(/\/$/, '').split('/').pop() ?? '';
  if (!objectId) return token;

  try {
    const { PRE_ACCESS_SHARE_LINK } = await import('@scio3/graphql-client');
    await client.query({
      query: PRE_ACCESS_SHARE_LINK,
      variables: { objectId, token },
      fetchPolicy: 'network-only',
    });
    // Force Firebase refresh after preAccess so the JWT carries the freshly
    // updated `shareLinkObjectIdTs` claim. Without this, manifest-driven SmallApp
    // calls (useSmallAppSave / useFieldAction / useFetchOptions / useOnLoad) would
    // get a cached sessionStorage token whose claim ts may be 30+ min stale,
    // causing SmallApp REST `getMe()` proxy → backend augment to skip and leak
    // the user's REAL accountId into the SmallApp microservice's MongoDB save
    // (cross-company asset.accountId mismatch).
    // Reference: claude-dev/2026-05-04-share-link-claim-stale/README.md
    const refreshed = await forceRefreshShareLinkToken(objectId);
    return refreshed ?? token;
  } catch (err) {
    // PRE_ACCESS or token refresh failed — caller continues with the unclaimed
    // main token. SmallApp REST may still 403 on cross-company asset endpoints.
    // Log so the failure is visible in the browser console for debugging.
    console.warn('[getSmallAppRestToken] claim refresh failed, using unclaimed token', err);
    return token;
  }
}
